import { ACTIVE_LOCATION_STORAGE_KEY } from '@jq4race/shared';
import { useSyncExternalStore } from 'react';

/**
 * Reactive view over the supervisor's active location (localStorage key
 * ACTIVE_LOCATION_STORAGE_KEY). localStorage stays the source of truth because
 * `@jq4race/api-client` reads it on every request to set `X-Location-Id`; this
 * store only adds change notification so React consumers re-render when
 * AppShell auto-selects a single assigned location (the page used to read the
 * key once during render and stayed stuck on "Select a location").
 */
const listeners = new Set<() => void>();

function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function getActiveLocationId(): string | null {
  return safeStorage()?.getItem(ACTIVE_LOCATION_STORAGE_KEY) ?? null;
}

export function setActiveLocationId(id: string | null): void {
  const storage = safeStorage();
  if (storage) {
    if (id === null) storage.removeItem(ACTIVE_LOCATION_STORAGE_KEY);
    else storage.setItem(ACTIVE_LOCATION_STORAGE_KEY, id);
  }
  for (const listener of listeners) listener();
}

export function subscribeActiveLocation(listener: () => void): () => void {
  listeners.add(listener);
  // Cross-tab changes.
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === ACTIVE_LOCATION_STORAGE_KEY) listener();
  };
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}

export function useActiveLocationId(): string | null {
  return useSyncExternalStore(subscribeActiveLocation, getActiveLocationId, () => null);
}
